import React from 'react'
import {
  Alert,
  Box,
  FormControlLabel,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'

import { ApifyIngestRequest } from '../../../hooks/useIngestSources'

/**
 * The scraper door: one video's or post's comments, into this job.
 *
 * Re-homed here from `datasource/` when ingest detached from pipelines. Two
 * things changed with the address:
 *
 *  - it edits the INGEST request (`platform`/`url`/`maxComments`/
 *    `includeReplies`), not a pipeline's `data_source` config. The endpoint
 *    takes a URL and works out the video's identity from it, so the old
 *    per-platform config objects had nothing left to hold.
 *  - the "Existing Dataset" tab is gone: pointing at an already-scraped Apify
 *    dataset is a pipeline data-source feature the job-scoped endpoint does not
 *    serve, and offering it here would be a door that leads nowhere.
 */

export const APIFY_PLATFORMS = ['tiktok', 'instagram', 'youtube', 'facebook'] as const
export type ApifyPlatform = (typeof APIFY_PLATFORMS)[number]

const PLATFORM_COPY: Record<
  ApifyPlatform,
  {
    label: string
    heading: string
    urlLabel: string
    placeholder: string
    hint: string
    /** Set when the platform's scraper has no replies switch: why it is off. */
    repliesFixed?: string
  }
> = {
  tiktok: {
    label: 'TikTok',
    heading: 'Scrape comments from a TikTok video',
    urlLabel: 'TikTok video URL',
    placeholder: 'https://www.tiktok.com/@username/video/123456789',
    hint: 'TikTok scraping typically takes 1-5 minutes depending on the number of comments.',
  },
  instagram: {
    label: 'Instagram',
    heading: 'Scrape comments from an Instagram post or reel',
    urlLabel: 'Instagram URL',
    placeholder: 'https://www.instagram.com/p/... or /reel/...',
    hint: 'Instagram scraping typically takes 1-5 minutes depending on the number of comments.',
  },
  youtube: {
    label: 'YouTube',
    heading: 'Scrape comments from a YouTube video',
    urlLabel: 'YouTube URL',
    placeholder: 'https://www.youtube.com/watch?v=...',
    hint: 'YouTube scraping typically takes 2-5 minutes depending on the number of comments.',
    repliesFixed: 'The YouTube scraper has no replies setting: the replies it finds come back with the comments.',
  },
  facebook: {
    label: 'Facebook',
    heading: 'Scrape comments from a Facebook post or video',
    urlLabel: 'Facebook URL',
    placeholder: 'https://www.facebook.com/<page>/posts/... or /videos/...',
    hint: 'Facebook scraping typically takes 1-5 minutes depending on the number of comments.',
  },
}

interface ApifyConfigProps {
  request: ApifyIngestRequest
  onChange: (request: ApifyIngestRequest) => void
  disabled?: boolean
}

const ApifyConfig: React.FC<ApifyConfigProps> = ({ request, onChange, disabled = false }) => {
  const platform = (APIFY_PLATFORMS.includes(request.platform as ApifyPlatform)
    ? request.platform
    : 'tiktok') as ApifyPlatform
  const copy = PLATFORM_COPY[platform]

  return (
    <Box>
      <Tabs
        value={platform}
        onChange={(_e, next: ApifyPlatform) => onChange({ ...request, platform: next })}
        sx={{ mb: 2 }}
      >
        {APIFY_PLATFORMS.map(p => (
          <Tab key={p} value={p} label={PLATFORM_COPY[p].label} disabled={disabled} />
        ))}
      </Tabs>

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {copy.heading}
      </Typography>

      <TextField
        fullWidth
        size="small"
        label={copy.urlLabel}
        placeholder={copy.placeholder}
        value={request.url || ''}
        onChange={e => onChange({ ...request, url: e.target.value })}
        disabled={disabled}
        sx={{ mb: 2 }}
        helperText="Paste the full URL — the comments and the video they belong to are read from it"
      />

      <TextField
        fullWidth
        size="small"
        label="Max comments (optional)"
        type="number"
        placeholder="Leave empty for all comments"
        value={request.maxComments || ''}
        onChange={e => onChange({ ...request, maxComments: parseInt(e.target.value, 10) || 0 })}
        disabled={disabled}
        sx={{ mb: 2 }}
        helperText="0 or empty means no limit"
      />

      <FormControlLabel
        control={
          <Switch
            checked={request.includeReplies || false}
            onChange={e => onChange({ ...request, includeReplies: e.target.checked })}
            disabled={disabled || Boolean(copy.repliesFixed)}
          />
        }
        label="Include reply comments"
      />
      {copy.repliesFixed && (
        <Typography variant="caption" color="text.secondary" component="p">
          {copy.repliesFixed}
        </Typography>
      )}

      <Alert severity="info" sx={{ mt: 2 }}>
        {copy.hint} The fetch runs in the background — it appears in the receipts list straight away
        and reports its outcome there when it finishes.
      </Alert>
    </Box>
  )
}

export default ApifyConfig
